import React, { useState, useEffect } from "react";
import {
  NumberInput,
  Checkbox,
  Button,
  Group,
  Paper,
  TextInput,
  Grid,
  Text,
  Modal,
  Stack,
  Select,
  Tooltip,
  Box,
  rem,
} from "@mantine/core";
import { observer } from "mobx-react";
import { Cubby } from "../stores/Cubby";

import {
  dustLimit,
  TEST_CHANNEL_OPENS_WITH_THIS_SATOSHIS,
} from "../helpers/constants";
import {
  maybeGetFeeEstimate,
  validateBitcoinAddress,
} from "../helpers/onchain";
import InfoCircle from "./dataDisplay/infoCircle";
import { TextWithInfo } from "./modals/textWithInfo";
import SatoshisInfo from "./interactiveData/satoshisInfo";

const SendFunds = observer(({ cubby }: { cubby: Cubby }) => {
  const [satoshis, setSatoshis] = useState(0);
  const [satoshisError, setSatoshisError] = useState("");
  const [sendAll, setSendAll] = useState(false);
  const [bitcoinAddress, setBitcoinAddress] = useState("");
  const [bitcoinAddressError, setBitcoinAddressError] = useState("");
  const [isSendDisabled, setIsSendDisabled] = useState(true);
  const [modalOpened, setModalOpened] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [selectedConfirmation, setSelectedConfirmation] = useState("average");

  useEffect(() => {
    const isSatoshisValid = sendAll || (satoshis > 1 && !satoshisError);
    const isBitcoinAddressValid = bitcoinAddress && !bitcoinAddressError;

    setIsSendDisabled(!(isSatoshisValid && isBitcoinAddressValid));
  }, [satoshis, sendAll, satoshisError, bitcoinAddress, bitcoinAddressError]);

  useEffect(() => {
    cubby.cubbyFastInfo("does_need_2FA_form");
    const satoshisAmount = Math.min(
      Math.ceil(cubby.safeChainBalance / 10),
      TEST_CHANNEL_OPENS_WITH_THIS_SATOSHIS
    );
    cubby.getFeeEstimateForArbitrarySatoshisAmount(satoshisAmount);
  }, []); // Run only once when the component mounts

  // console.log(
  //   "in sendFunds.tsx",
  //   "cubby.lastOnchainFeeEstimates",
  //   cubby.lastOnchainFeeEstimates.data
  // );

  // console.log(
  //   "cubby.amountThatCouldBeSentWithoutSendAll",
  //   cubby.amountThatCouldBeSentWithoutSendAll
  // );

  const makeSendFundsToolTip = () => {
    if (cubby.numberOfUnclosedChannelsOpenedByUser > 0) {
      return "You have open channels. As a safety measure, we don't let you send all funds from your wallet until your channels are closed.";
    }
    return "Use with caution. This will pay any fees necessary to send all your funds.";
  };

  const handleSatoshisChange = (newValue: number) => {
    let tempSatoshisError = "";

    if (!sendAll) {
      if (newValue < dustLimit) {
        tempSatoshisError = "Minimum " + dustLimit + " satoshis";
      } else if (newValue > cubby.amountThatCouldBeSentWithoutSendAll) {
        if (cubby.canUseSendAll == true) {
          tempSatoshisError = `To send more than ${cubby.amountThatCouldBeSentWithoutSendAll.toLocaleString()} satoshis, use "Send All Funds"`;
        } else {
          tempSatoshisError = `To send more than ${cubby.amountThatCouldBeSentWithoutSendAll.toLocaleString()} satoshis, close your channels first`;
        }
      } else {
        tempSatoshisError = "";
      }
      setSatoshisError(tempSatoshisError);
      setSatoshis(newValue);
      console.log("tempSatoshisError", tempSatoshisError, "newValue", newValue);
      console.log("in maybe get fee estimate in handleSatoshisChange");
      // cubby.resetLastOnchainFeeEstimates();
      maybeGetFeeEstimate(
        newValue,
        bitcoinAddress,
        tempSatoshisError,
        bitcoinAddressError,
        sendAll,
        cubby
      );
    }
  };

  const handleBitcoinAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newAddress = event.currentTarget.value;
    setBitcoinAddress(newAddress);

    let tempBitcoinAddressError = "";

    const isValid = validateBitcoinAddress(cubby, newAddress);

    if (!isValid) {
      tempBitcoinAddressError = "Invalid Bitcoin address";
      setBitcoinAddressError(tempBitcoinAddressError);
    } else {
      tempBitcoinAddressError = "";
      setBitcoinAddressError(tempBitcoinAddressError);
    }
    console.log("in maybe get fee estimate in handleBitcoinAddressChange");
    maybeGetFeeEstimate(
      sendAll ? null : satoshis, // If sendAll is true, pass null for satoshis
      newAddress,
      satoshisError,
      tempBitcoinAddressError,
      sendAll,
      cubby
    );
  };

  const handleSendAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    setSendAll(checked);
    if (checked) {
      const balance = cubby.safeChainBalance;
      setSatoshis(balance);
      setSatoshisError("");
      maybeGetFeeEstimate(
        null, // Pass null for satoshis if sending all funds
        bitcoinAddress,
        "",
        bitcoinAddressError,
        true, // Indicate that sendAll is true
        cubby
      );
    } else {
      setSatoshis(0);
    }
  };

  const handleOpenModal = () => {
    setModalOpened(true);
  };

  const handleConfirmSend = () => {
    // Determine targetConfirmations based on selectedConfirmation
    const targetConfirmations = (() => {
      switch (selectedConfirmation) {
        case "fastest":
          return 1;
        case "fast":
          return 3;
        case "average":
          return 6;
        case "slow":
          return 12;
        default:
          return 6; // Default to "average" if something goes wrong
      }
    })();

    // Handle the sending of funds, for example:
    console.log(
      `Sending ${
        sendAll ? cubby.safeChainBalance : satoshis
      } satoshis to ${bitcoinAddress} with 2FA code: ${twoFACode}`
    );
    const data = {
      amountInSatoshis: sendAll ? null : satoshis,
      destinationAddress: bitcoinAddress,
      twoFactorCode: twoFACode,
      sendAll: sendAll,
      targetConfirmations: targetConfirmations,
    };
    cubby.cubbyLife("dangerous_send_payment", data);
    setModalOpened(false);
    cubby.setModal(null);
  };

  const confirmationOptions = [
    { value: "fastest", label: "less than 15 minutes" },
    { value: "fast", label: "less than 30 minutes" },
    { value: "average", label: "less than one hour" },
    { value: "slow", label: "less than two hours" },
  ];

  const selectedFeeEstimate = cubby.lastOnchainFeeEstimates.data.find(
    (estimate) => {
      switch (selectedConfirmation) {
        case "fastest":
          return estimate.confirmations === 1;
        case "fast":
          return estimate.confirmations === 3;
        case "average":
          return estimate.confirmations === 6;
        case "slow":
          return estimate.confirmations === 12;
        default:
          return false;
      }
    }
  );
  console.log(
    "cubby.amountThatCouldBeSentWithoutSendAll",
    cubby.amountThatCouldBeSentWithoutSendAll
  );

  return (
    <Paper p="md" shadow="sm">
      <Text mb="md" size="xs">
        Onchain payments usually confirm in about 10 to 30 minutes.
      </Text>

      <>
        {cubby.sendFundsDirectiveAndWarning.allow == true && (
          <form>
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                {cubby.amountThatCouldBeSentWithoutSendAll > 0 && (
                  <>
                    <NumberInput
                      label="Satoshis"
                      description="Enter the amount of satoshis to send"
                      value={sendAll ? cubby.safeChainBalance : satoshis}
                      onChange={handleSatoshisChange}
                      error={satoshisError}
                      min={1}
                      disabled={sendAll}
                      required
                    />
                    {!sendAll && (
                      <SatoshisInfo cubby={cubby} satoshis={satoshis} />
                    )}
                  </>
                )}
                {cubby.amountThatCouldBeSentWithoutSendAll === 0 &&
                  !sendAll && (
                    <Text size="sm" c="dimmed">
                      Your onchain balance is only{" "}
                      {cubby.safeChainBalance.toLocaleString()} satoshis. To
                      send a payment, select Send All Funds.
                    </Text>
                  )}
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Bitcoin Address"
                  description="Enter the Bitcoin address to send funds to"
                  value={bitcoinAddress}
                  onChange={handleBitcoinAddressChange}
                  error={bitcoinAddressError}
                  required
                />
              </Grid.Col>
            </Grid>
            <Group justify="space-between" mt="md">
              <Tooltip multiline w={300} label={makeSendFundsToolTip()}>
                <Checkbox
                  label={
                    "Send All Funds"
                    // <TextWithInfo
                    //   textSize="sm"
                    //   text="Send All Funds"
                    //   infoLink="https://docs.megalithic.me"
                    //   infoCircleNudge={{ vertical: -2, horizontal: -6 }}
                    // />
                  }
                  checked={sendAll}
                  onChange={handleSendAllChange}
                  disabled={cubby.canUseSendAll == false}
                />
              </Tooltip>
              {!sendAll && (
                <Select
                  label={
                    <TextWithInfo
                      textSize="sm"
                      text="Transaction Confirmation Time"
                      infoLink="https://docs.megalithic.me"
                      infoCircleNudge={{ vertical: -2, horizontal: -6 }}
                    />
                  }
                  placeholder="Pick value"
                  data={confirmationOptions}
                  value={selectedConfirmation}
                  onChange={setSelectedConfirmation}
                  clearable
                />
              )}
              {selectedFeeEstimate && (
                <Stack>
                  {/* <Text size="sm">
                   
                  </Text> */}

                  <TextWithInfo
                    textSize="sm"
                    text={
                      "fee estimate:  " +
                      selectedFeeEstimate.fee.toLocaleString() +
                      " satoshis"
                    }
                    infoLink="https://docs.megalithic.me"
                    infoCircleNudge={{ vertical: -2, horizontal: -6 }}
                  />
                </Stack>
              )}
              {sendAll && (
                <Text size="sm" c="dimmed">
                  We can't estimate fees when sending all funds
                </Text>
              )}
              <Button onClick={handleOpenModal} disabled={isSendDisabled}>
                Send
              </Button>
            </Group>
          </form>
        )}
        {cubby.sendFundsDirectiveAndWarning.allow == false && (
          <Text c="red">{cubby.sendFundsDirectiveAndWarning.error}</Text>
        )}
      </>
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Confirm Transaction"
        centered
      >
        <Stack>
          <Text>
            You are going to send{" "}
            {(sendAll ? cubby.safeChainBalance : satoshis).toLocaleString()}{" "}
            satoshis (
            {sendAll
              ? cubby.safeChainBalance / 100000000
              : satoshis / 100000000}{" "}
            BTC) to this address:
          </Text>

          <Text size="xs">{bitcoinAddress}</Text>
          {!sendAll && selectedFeeEstimate && (
            <Text>
              You will pay about {selectedFeeEstimate.fee.toLocaleString()}{" "}
              satoshis in fees.
            </Text>
          )}

          <Text>Be careful: This action can't be reversed!</Text>
        </Stack>

        {cubby.shouldShow2FAFormForPayments && (
          <>
            <TextInput
              label="Enter 2FA Code"
              //placeholder="2FA Code should be six number digits"
              maxLength={7}
              value={twoFACode}
              onChange={(event) => setTwoFACode(event.currentTarget.value)}
              mt="md"
            />
            <Text size="xs" c="dimmed">
              Get the code from your authenticator app
            </Text>
          </>
        )}
        <Group justify="flex-end" mt="lg">
          <Button variant="outline" onClick={() => setModalOpened(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              cubby.shouldShow2FAFormForPayments && twoFACode.length < 6
            }
            color="red"
            onClick={handleConfirmSend}
          >
            Submit
          </Button>
        </Group>
      </Modal>
    </Paper>
  );
});

export default SendFunds;
